<template>
  <div class="border-b px-4 py-8 md:px-8">
    <p class="text-[16px] md:text-[20px]">1. Select Preferred Date</p>
    <MoleculesFormInputWrapper v-if="isCalendarVisible" class="mt-[-8px] pt-2" label="" lang="en" :error="getErrorMessage('step1.selectedDate')" :required="false">
      <div class="bg-[#F4F1ED]">
        <OrganismsExperienceCalendar
          :key="props.calendarRefreshKey"
          data-testid="experience-calendar-in-drawer"
          :calendar-events="calendarEvents"
          :display-month="displayMonth"
          :is-error="getErrorMessage('step1.selectedDate') !== ''"
          :is-loading="isLoading"
          :on-change-month="onChangeMonth"
          :on-click-date="onClickDateAndCloseCalendar"
          :selected-date="reservationFormStep1.selectedDate ?? undefined"
        />
        <OrganismsCalendarCaption date-positioning-class="top-[0.37rem]">
          <template v-if="hasCalendarNote" #note>
            <p class="whitespace-pre-line break-words pb-2 text-sm text-black2" data-testid="calendar-note">{{ calendarNoteText }}</p>
          </template>
        </OrganismsCalendarCaption>
      </div>
    </MoleculesFormInputWrapper>
    <div v-else class="mt-2 flex items-center justify-between border" data-testid="selected-date-in-calendar-picker">
      <span class="mt-0.5 cursor-pointer rounded-lg p-3 hover:bg-gray5" data-testid="previous-button" @click="onClickPreviousButton">
        <img alt="The day before" class="h-[18px] w-[10px]" src="~@/assets/img/parts/top-left.svg" />
      </span>
      <p class="flex items-center gap-x-2 text-[17px] text-[#525252]">
        <span class="pt-1">{{ formattedSelectedDate }}</span>
        <img alt="select date" class="m-0 h-6 w-6 cursor-pointer p-0" data-testid="select-date-button" :src="calendarIconUrl" @click="isCalendarOpen = !isCalendarOpen" />
      </p>
      <span class="mt-0.5 cursor-pointer rounded-lg p-3 hover:bg-gray5" data-testid="next-button" @click="onClickNextButton">
        <img alt="The day after" class="h-[18px] w-[10px]" src="~@/assets/img/parts/top-right.svg" />
      </span>
    </div>
    <WaitlistRegistrationCta v-if="props.isWaitlistEnabled && isCalendarVisible" button-test-id="waitlist-registration-cta-in-drawer" class="mt-5" :on-click="props.onClickGetNotified" />
  </div>
</template>

<script lang="ts" setup>
import MoleculesFormInputWrapper from '@/components/molecules/FormInputWrapper/FormInputWrapper.vue';
// public/ 配下の画像参照は :src バインディング経由で行う（コンパイル時 import 化を回避、JCAT-4981）
const calendarIconUrl = '/img/calendar/available.svg';
import OrganismsCalendarCaption from '@/components/molecules/wabunka/CalendarCaption/CalendarCaption.vue';
import OrganismsExperienceCalendar from '@/components/organisms/pages/en/experiences/detail/ExperienceCalendar/ExperienceCalendar.vue';
import { formatDateInEnLocale, getCurrentJstDayjs } from '@/utils/date';
import type { WabunkaExperienceEventDictViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import WaitlistRegistrationCta from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/WaitlistRegistrationCta/WaitlistRegistrationCta.vue';
import { useCalendarNote } from '@/wabunka-src/features/Experience/pages/ExperienceDetail/composables/general/useCalendarNote';
import type { WabunkaReservationFormStep1 } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaReservations';

import type dayjs from 'dayjs';

interface Props {
  /**
   * 他箇所での変更分をカレンダーに反映するための再描画のキー
   */
  calendarRefreshKey: number;
  displayMonth?: dayjs.Dayjs;
  isLoading?: boolean;
  calendarEvents: WabunkaExperienceEventDictViewModel;
  onChangeMonth: (startDate: string, endDate: string) => void;
  onClickDate: (date: dayjs.Dayjs) => void;
  onClickPreviousButton: () => void;
  onClickNextButton: () => void;
  getErrorMessage: (s: string) => string;
  reservationFormStep1: WabunkaReservationFormStep1;
  isSelectedDate: boolean;
  calendarNote?: string;
  onClickGetNotified: () => void;
  /**
   * ウェイトリスト登録の露出制御。フラグ OFF の間は CTA を描画しない
   */
  isWaitlistEnabled: boolean;
}

const props = defineProps<Props>();
const { calendarNoteText, hasCalendarNote } = useCalendarNote(() => props.calendarNote);
const isCalendarOpen = ref(false);
// 日付選択済みでもカレンダーを開き直せるため、開閉は選択状態と手動開閉の両方で決まる
const isCalendarVisible = computed(() => !props.isSelectedDate || isCalendarOpen.value);
const formattedSelectedDate = computed(() => formatDateInEnLocale((props.reservationFormStep1.selectedDate ?? getCurrentJstDayjs()).toDate()));
const onClickDateAndCloseCalendar = (date: dayjs.Dayjs) => {
  props.onClickDate(date);
  isCalendarOpen.value = false;
};
</script>
